import { BoostProvider, BoostTask } from '../types/boost';
import {
  getAdLink,
  getAdWidget,
  getAdWidgetLink,
} from '@/api/modules/Tasks/barzha';

export class BarzaBoostProvider implements BoostProvider {
  private static instance: BarzaBoostProvider;
  private hasError: boolean = false;
  private initializationAttempts = 0;
  private readonly MAX_INIT_ATTEMPTS = 5;
  private readonly INIT_RETRY_DELAY = 1000;
  private lastTaskTime: number = 0;
  private readonly TASK_COOLDOWN = 5000;
  private webAppInitialized: boolean = false;

  private constructor() {
    // Инициализируем WebApp при создании инстанса
    this.initializeWebApp();
  }

  public static getInstance(): BarzaBoostProvider {
    if (!BarzaBoostProvider.instance) {
      BarzaBoostProvider.instance = new BarzaBoostProvider();
    }
    return BarzaBoostProvider.instance;
  }

  private async initializeWebApp() {
    // Проверяем, доступен ли WebApp сразу
    if (window.Telegram?.WebApp) {
      this.webAppInitialized = true;
      console.log('WebApp available immediately');
      return;
    }

    // Ждем событие загрузки страницы
    if (document.readyState === 'loading') {
      await new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve);
      });
    }

    // Пытаемся получить WebApp после загрузки страницы
    if (window.Telegram?.WebApp) {
      this.webAppInitialized = true;
      console.log('WebApp available after DOMContentLoaded');
      return;
    }

    console.log('WebApp not available after page load, will check on demand');
  }

  private canShowNewTask(): boolean {
    return Date.now() - this.lastTaskTime >= this.TASK_COOLDOWN;
  }

  private async waitForTelegramWebApp(): Promise<boolean> {
    console.log(
      `BarzaBoostProvider: Checking WebApp state. Current flag: ${this.webAppInitialized}`,
    );

    // Всегда сначала проверяем реальный объект
    try {
      if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
        console.log('BarzaBoostProvider: window.Telegram.WebApp seems ready.');
        this.webAppInitialized = true; // Обновляем флаг
        return true;
      }
    } catch (e) {
      console.log('BarzaBoostProvider: Initial check failed:', e);
      this.webAppInitialized = false; // Сбрасываем флаг при ошибке
    }

    // Если не готово, начинаем цикл ожидания
    console.log(
      'BarzaBoostProvider: WebApp not immediately ready, starting wait loop...',
    );
    this.initializationAttempts = 0; // Сбрасываем счетчик для этого цикла

    return new Promise((resolve) => {
      const checkWebApp = () => {
        try {
          // Проверяем снова внутри цикла
          if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
            console.log(
              'BarzaBoostProvider: Telegram WebApp initialized successfully during wait.',
            );
            this.webAppInitialized = true;
            this.initializationAttempts = 0;
            resolve(true);
            return;
          }
        } catch (e) {
          console.log('BarzaBoostProvider: Wait loop check failed:', e);
        }

        this.initializationAttempts++;

        if (this.initializationAttempts < this.MAX_INIT_ATTEMPTS) {
          console.log(
            `BarzaBoostProvider: Waiting for Telegram WebApp initialization... (attempt ${this.initializationAttempts}/${this.MAX_INIT_ATTEMPTS})`,
          );
          setTimeout(checkWebApp, this.INIT_RETRY_DELAY);
        } else {
          console.log(
            'BarzaBoostProvider: Failed to initialize Telegram WebApp after waiting.',
          );
          this.webAppInitialized = false;
          resolve(false);
        }
      };
      checkWebApp(); // Запускаем проверку
    });
  }

  private isTelegramUrl(url: string): boolean {
    if (typeof url !== 'string') {
      return false;
    }
    return url.includes('t.me/');
  }

  async startTask(
    taskId: string | number,
  ): Promise<'success' | 'ads_not_found' | 'user_cancelled'> {
    if (!this.canShowNewTask()) {
      console.log('Cannot start Barza task: on cooldown');
      return 'ads_not_found';
    }

    const isWebAppReady = await this.waitForTelegramWebApp();
    if (!isWebAppReady) {
      console.log('Cannot start Barza task: Telegram WebApp not initialized');
      return 'ads_not_found';
    }

    try {
      console.log('taskIDDDD,', taskId);
      const widgetResponse = await getAdLink(
        Number(taskId),
        'test',
        'test',
        'test',
      );

      if (widgetResponse?.status === 'error') {
        console.error('Error getting Barza widget link:', widgetResponse.error);
        return 'ads_not_found';
      }

      if (!widgetResponse?.link) {
        console.log('Cannot start Barza task: no valid widget URL');
        return 'ads_not_found';
      }

      const widgetLink = widgetResponse.link;
      if (this.isTelegramUrl(widgetLink)) {
        try {
          window?.Telegram?.WebApp?.openTelegramLink(widgetLink);
          this.lastTaskTime = Date.now();
          this.webAppInitialized = false;
          return 'success';
        } catch (error) {
          console.error('Error opening Telegram link:', error);
          this.hasError = true;
          return 'ads_not_found';
        }
      } else {
        window.open(widgetLink, '_blank');
        this.lastTaskTime = Date.now();
        this.webAppInitialized = false;
        return 'success';
      }
    } catch (error) {
      console.error('Error starting Barza task:', error);
      this.hasError = true;
      return 'ads_not_found';
    }
  }

  async getAvailableTasks(): Promise<BoostTask[]> {
    if (this.hasError) {
      return [];
    }

    // Проверяем доступность WebApp
    const isWebAppReady = await this.waitForTelegramWebApp();
    if (!isWebAppReady) {
      console.log('Telegram WebApp not initialized, skipping Barza tasks');
      return [];
    }

    try {
      const widget = await getAdWidget();
      console.log('Barza response:', widget);

      if (widget.status !== 'success' || !widget.ad_campaign_id) {
        console.log('No valid Barza tasks found');
        return [];
      }

      const task: BoostTask = {
        id: `${widget.ad_campaign_id}`,
        title: widget.title || 'Просмотреть рекламу',
        description: widget.description || '',
        provider: 'Barza',
        icon: widget.image_href
          ? `${import.meta.env.VITE_BARZHA_DOMAIN_URL}${widget.image_href}`
          : undefined,
        onClick: () => this.startTask(widget.ad_campaign_id),
      };

      return [task];
    } catch (error) {
      console.error('Error getting Barza tasks:', error);
      this.hasError = true;
      return [];
    }
  }

  getName(): string {
    return 'Barza';
  }

  resetError() {
    this.hasError = false;
    this.initializationAttempts = 0;
    this.lastTaskTime = 0;
    this.webAppInitialized = false;
  }
}

// Экспортируем уже созданный экземпляр
export const barzaBoostProvider = BarzaBoostProvider.getInstance();
